import {
    FC,
    ReactElement,
    useEffect,
    useState,
} from 'react'

import { useDispatch } from 'react-redux'

import { authenticateUser } from '../../../redux/authentication/authenticationActions'
import { Button, Icon } from '../../components'
import { LoginLayout } from '../../containers'
import { LoginFormData } from '../../entities/@forms/LoginForm/LoginForm'
import { isMicrosoftSsoEnabled } from '../../helpers/featureFlags'
import trans from '../../helpers/trans'
import { useTypedSelector } from '../../redux/store'
import { navigateToSamlLogin } from '../../services/samlLoginService'
import { ApiLogin, MicrosoftLogin } from './subcomponents'
import { LoginFormErrors, validateLoginFormData } from './validations'

import './Login.scss'

const Login: FC = (): ReactElement => {
    const dispatch = useDispatch()

    const {
        isLoading: isApiLoginLoading,
        authenticateFailure: apiLoginAuthenticateFailure,
    } = useTypedSelector(state => state.authenticationReducer)

    const microsoftSsoEnabled = isMicrosoftSsoEnabled()

    const samlError = new URLSearchParams(window.location.search).get('saml-error')

    const [showApiLogin, setShowApiLogin] = useState<boolean>(false)

    const [apiLoginFormErrors, setApiLoginFormErrors] = useState<LoginFormErrors>({})

    const [microsoftLoginAuthenticationError, setMicrosoftLoginAuthenticationError] = useState<string | undefined>(samlError ?? undefined)
    const [isMicrosoftLoginLoading, setIsMicrosoftLoginLoading] = useState<boolean>(false)

    const handleApiLogin = (formData: LoginFormData): void => {
        const [errors, hasErrors] = validateLoginFormData(formData)

        setApiLoginFormErrors(errors)

        if (! hasErrors) {
            dispatch(authenticateUser(formData))
        }
    }

    const handleMicrosoftLogin = (): void => {
        if (isMicrosoftLoginLoading) return

        setMicrosoftLoginAuthenticationError(undefined)
        setIsMicrosoftLoginLoading(true)

        navigateToSamlLogin()
    }

    // Remove `saml-error` query param from url after showing the error message (if present)
    useEffect(() => {
        if (samlError && ! microsoftLoginAuthenticationError) {
            const url = new URL(window.location.href)
            url.searchParams.delete('saml-error')
            window.history.replaceState({}, document.title, url.toString())
        }
    }, [microsoftLoginAuthenticationError])

    return (
        <LoginLayout>
            <div className="login-page">
                <h2 className="login-page__title">
                    {trans('pages.login.login')}
                </h2>

                <div className="login-page__login-options">
                    {microsoftSsoEnabled && (
                        <MicrosoftLogin
                            isLoading={isMicrosoftLoginLoading}
                            onSubmit={handleMicrosoftLogin}
                            authenticationErrorMessage={microsoftLoginAuthenticationError ? trans(`pages.login.${microsoftLoginAuthenticationError}`) : undefined}
                            className="login-page__microsoft-login"
                        />
                    )}

                    {microsoftSsoEnabled && ! showApiLogin && (
                        <Button color="link"
                                text="Inloggen met gebruikersnaam en wachtwoord"
                                onClick={() => setShowApiLogin(true)}
                                className="login-page__show-api-login-button"
                        >
                            <span>Inloggen met gebruikersnaam en wachtwoord</span>
                            <Icon className="dashboard-small-widget-content__button-icon" name="chevron-right" />
                        </Button>
                    )}

                    {(! microsoftSsoEnabled || showApiLogin) && (
                        <ApiLogin
                            isLoading={isApiLoginLoading}
                            authenticateFailure={apiLoginAuthenticateFailure}
                            formErrors={apiLoginFormErrors}
                            onSubmit={handleApiLogin}
                            className="login-page__api-login"
                        />
                    )}
                </div>
            </div>
        </LoginLayout>
    )
}

export default Login
